/**
 * StagedBuildProgress — Stage B wait card.
 *
 * Honest, staged progress for the "build a culminating task, then
 * task-aware lessons" flow. Instead of a bare spinner it states the
 * real cost up front ("about a minute and a half") and shows which of
 * the two stages is in flight, driven by the backend `progress`
 * waypoint the poller surfaces.
 *
 * The build runs server-side regardless of this tab, so the card also
 * reassures the teacher they can leave and it'll land in My Library.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Sparkles, Target } from "lucide-react";

// Decide which stage is active from the backend waypoint string.
// Stage 1 = drafting the culminating task; Stage 2 = building lessons.
const _stageFromProgress = (progress) => {
  const p = (progress || "").toLowerCase();
  if (!p) return 1;
  if (p.includes("lesson") || p.includes("unit —") || p.includes("minute and a half")) return 2;
  if (p.includes("task")) return 1;
  return 1;
};

const StepRow = ({ index, label, active, done }) => (
  <li
    className="flex items-center gap-3"
    data-testid={`staged-build-step-${index}`}
    data-active={active ? "true" : "false"}
    data-done={done ? "true" : "false"}
  >
    <span
      className={`h-7 w-7 shrink-0 grid place-items-center rounded-full border-2 text-[11px] font-black transition-colors ${
        done
          ? "border-[#1c9e7e] bg-[#1c9e7e] text-white"
          : active
          ? "border-[#1c2d3d] bg-white text-[#1c2d3d]"
          : "border-[#4a6984]/25 bg-white text-[#4a6984]/50"
      }`}
    >
      {done ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : active ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        index
      )}
    </span>
    <span
      className={`text-sm font-bold tracking-tight ${
        done ? "text-[#4a6984]" : active ? "text-[#1c2d3d]" : "text-[#4a6984]/55"
      }`}
    >
      {label}
    </span>
  </li>
);

const StagedBuildProgress = ({
  progress,
  estimateSeconds = 95,
  startedAt,
  numLessons,
  model = "standalone",
}) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt) return undefined;
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const hasTaskStage = model === "standalone" || model === "distributed";
  const taskOnly = model === "task_only";
  const stage = _stageFromProgress(progress);
  const lessonsLabel = numLessons
    ? `Building your ${numLessons}-lesson unit`
    : "Building your unit";
  // The Ongoing (distributed) model isn't a single end-of-unit "culminating"
  // task — name it honestly on the progress card.
  const taskNoun = model === "distributed" ? "ongoing task" : "culminating task";

  // Waypoint line: prefer the live backend waypoint; else a calm default.
  const waypoint =
    (progress && progress.trim()) ||
    (hasTaskStage || taskOnly
      ? `Drafting your ${taskNoun}…`
      : "Setting up your unit…");

  const heading = taskOnly
    ? "Building your culminating task"
    : hasTaskStage
    ? "Building your task-aligned unit"
    : "Building your unit";

  const estimateCopy = taskOnly
    ? "This takes about 30–45 seconds. It's grounded in the lessons you already built — your lessons aren't changed."
    : "This takes about a minute and a half. You can leave this page — we'll keep building and it'll be in your Library when you return.";

  return (
    <div
      className="rounded-2xl border border-[#96ded1]/60 bg-white shadow-xl shadow-[#4a6984]/5 p-6 sm:p-8"
      data-testid="staged-build-progress"
    >
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 shrink-0 grid place-items-center rounded-xl bg-[#96ded1]/30 text-[#1c2d3d]">
          <Sparkles className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h3 className="font-black text-lg sm:text-xl text-[#1c2d3d] tracking-tight">
            {heading}
          </h3>
          <p
            className="mt-1 text-sm text-[#4a6984] font-semibold"
            data-testid="staged-build-estimate"
          >
            {estimateCopy}
          </p>
        </div>
      </div>

      {hasTaskStage ? (
        <ul className="mt-6 space-y-3" data-testid="staged-build-steps">
          <StepRow
            index={1}
            label={`Drafting your ${taskNoun}`}
            active={stage === 1}
            done={stage > 1}
          />
          <StepRow
            index={2}
            label={`${lessonsLabel} (aware of the task)`}
            active={stage === 2}
            done={false}
          />
        </ul>
      ) : taskOnly ? (
        <ul className="mt-6 space-y-3" data-testid="staged-build-steps">
          <StepRow
            index={1}
            label="Drafting your culminating task, grounded in your taught lessons"
            active
            done={false}
          />
        </ul>
      ) : (
        <ul className="mt-6 space-y-3" data-testid="staged-build-steps">
          <StepRow index={1} label={lessonsLabel} active done={false} />
        </ul>
      )}

      <div className="mt-6 flex items-center gap-2 rounded-lg bg-[#f4f5f7] px-3 py-2.5">
        <Target className="h-3.5 w-3.5 text-[#1c9e7e] shrink-0" />
        <p
          className="text-xs font-bold text-[#4a6984] truncate"
          data-testid="staged-build-waypoint"
        >
          {waypoint}
        </p>
        {startedAt ? (
          <span
            className="ml-auto text-[11px] font-bold text-[#4a6984]/60 tabular-nums shrink-0"
            data-testid="staged-build-elapsed"
          >
            {elapsed}s / ~{estimateSeconds}s
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default StagedBuildProgress;
